import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — BÁN HÀNG: HÓA ĐƠN · THU TIỀN · CÔNG NỢ (F15) ═══════════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/ar/*` và hai cửa yêu cầu xuất hóa đơn trên đơn.
 */

export const BillMode = z.enum(['immediate', 'monthly'])
export type BillMode = z.infer<typeof BillMode>

/**
 * YÊU CẦU XUẤT HÓA ĐƠN cho một đơn.
 *
 * ⚠ `split` là MẢNG CHUỖI TIỀN, đúng HAI phần tử khi có tách. Tổng hai phần phải bằng đúng tổng
 *   đơn — máy chủ kiểm, không tin màn hình (`splitError`).
 */
export const RequestBillBody = z.object({
  version: Version,
  mode: BillMode,
  split: z.array(moneyField('Số tiền tách')).max(2, 'Chỉ tách được thành ĐÚNG HAI hóa đơn.').optional(),
  note: blankAsAbsent(z.string()),
  invName: blankAsAbsent(z.string()),
  invTax: blankAsAbsent(z.string()),
  invAddress: blankAsAbsent(z.string()),
})

export const CancelBillBody = z.object({ version: Version })

/**
 * 🧾 YÊU CẦU XUẤT CHỨNG TỪ BÁN NHIỀU ĐƠN (`BillRequestModal`) — lập NGAY bảng kê nháp; tách hóa đơn ⇒ hai bảng kê.
 * `splits` có thì PHẢI đủ mọi đơn; `inv2CustomerId` chỉ đi cùng `splits`.
 */
export const StatementRequestBody = z.object({
  jobIds: z.array(Uuid).min(1, 'Chưa chọn đơn nào.').max(200),
  /**
   * ⭐ `monthly` = GOM CUỐI THÁNG: đánh dấu đơn đã xin xuất, nhưng KHÔNG lập bảng kê và KHÔNG cấp
   *   mã — bảng kê dựng sau, lúc chốt tháng. Vắng ⇒ `immediate`, đúng hành vi trước đây.
   * ⚠ Gom cuối tháng thì KHÔNG tách hoá đơn được: tách sinh ra HAI bảng kê, mà chế độ này chưa lập
   *   bảng kê nào. Máy chủ từ chối thay vì lặng lẽ bỏ phần tách.
   */
  mode: BillMode.optional(),
  invName: blankAsAbsent(z.string()),
  invTax: blankAsAbsent(z.string()),
  invAddress: blankAsAbsent(z.string()),
  splits: z.array(z.object({
    jobId: Uuid,
    parts: z.tuple([moneyField('Số tiền HĐ #1'), moneyField('Số tiền HĐ #2')]),
  })).optional(),
  inv2CustomerId: blankAsAbsent(Uuid),
  note: blankAsAbsent(z.string()),
  stmtDate: blankAsAbsent(ISODate),
})

export const StatementRequestPreviewQuery = z.object({
  /** Mã đơn, phân tách bằng dấu phẩy. */
  jobIds: z.string().min(1),
})

export const CancelInvoiceBody = z.object({
  version: Version,
  reason: z.string().min(1, 'Phải ghi lý do huỷ hóa đơn — số chứng từ đã cấp thì phải giải trình được.'),
})

export const SetInvoiceNoBody = z.object({
  version: Version,
  invoiceNo: z.string().min(1, 'Chưa nhập số hóa đơn GTGT.'),
})

export const PaymentDeleteBody = z.object({ version: Version })

/* ══ CỌC / KHÁCH TRẢ TRƯỚC ═════════════════════════════════════════════════════════════════════ */

export const NewAdvanceBody = z.object({
  customerId: Uuid,
  recvDate: ISODate,
  amount: moneyField('Số tiền cọc'),
  method: z.enum(['bank', 'cash', 'offset']).optional(),
  bankAccountId: blankAsAbsent(Uuid),
  note: blankAsAbsent(z.string()),
})

/**
 * ⚠ GỬI SỐ DÒNG CẤN, và máy chủ vẫn KIỂM LẠI cọc còn bao nhiêu trước khi ghi: giữa lúc hộp mở ra
 *   và lúc bấm, người khác có thể đã cấn chính cọc đó ở màn khác.
 */
export const AllocAdvanceBody = z.object({
  lines: z.array(z.object({ invoiceId: Uuid, amount: moneyField('Số tiền cấn') }))
    .min(1, 'Chưa nhập số tiền cấn cho hoá đơn nào.').max(500, 'Tối đa 500 hoá đơn mỗi lượt.'),
  asOf: blankAsAbsent(ISODate),
})

export const AdvanceListQuery = z.object({
  customerId: blankAsAbsent(Uuid),
  openOnly: z.coerce.boolean().optional(),
})

export const AdvanceIdParams = z.object({ id: Uuid })
export type NewAdvanceBodyT = z.infer<typeof NewAdvanceBody>
export type AllocAdvanceBodyT = z.infer<typeof AllocAdvanceBody>
export type AdvanceListQueryT = z.infer<typeof AdvanceListQuery>

/** 🧾 Sửa phiếu chứng từ bán hàng — phần KHÔNG đụng tiền (`DebtTab`). `null` = xoá, vắng khoá = không đụng. */
export const UpdateArDocBody = z.object({
  version: Version,
  docDate: blankAsAbsent(ISODate),
  postDate: blankAsAbsent(ISODate),
  invoiceNo: z.string().max(100).nullable().optional(),
  invoiceDate: blankAsAbsent(ISODate.nullable()),
  invoiceSymbol: z.string().max(50).nullable().optional(),
  invoiceFormNo: z.string().max(50).nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
  amisIssuedInvoice: z.boolean().optional(),
  lines: z.array(z.object({
    id: Uuid,
    itemCode: z.string().max(100).nullable().optional(),
    label: z.string().max(500).optional(),
    jobRef: z.string().max(100).nullable().optional(),
    amisServiceCode: z.string().max(100).nullable().optional(),
    plate: z.string().max(50).nullable().optional(),
    acctReceivable: z.string().max(20).nullable().optional(),
    acctRevenue: z.string().max(20).nullable().optional(),
    acctVat: z.string().max(20).nullable().optional(),
  })).max(500).optional(),
  confirmPushed: z.boolean().optional(),
})
export type UpdateArDocBodyT = z.infer<typeof UpdateArDocBody>

/** Hàng loạt trên các hóa đơn đang tick — đẩy AMIS · ngày hóa đơn = ngày chứng từ. */
export const ArDocIdsBody = z.object({ ids: z.array(Uuid).min(1, 'Chưa chọn hóa đơn nào.').max(200) })
export type ArDocIdsBodyT = z.infer<typeof ArDocIdsBody>
export const ArDocNoBody = z.object({})

/**
 * ══ BỘ LỌC CỦA SỔ CHỨNG TỪ BÁN — TẤT CẢ chạy ở MÁY CHỦ (§8.3) ════════════════════════════════
 *
 * Trước đợt này màn tải HẾT mọi trang rồi mới lọc bằng JS, tức phân trang có mà như không. Bảy ô
 * lọc của màn (`ArDocTab`) nay xuống tận SQL.
 *
 * ⚠ Hộp chọn NHIỀU đi trên dây bằng chuỗi ngăn bằng dấu phẩy, không phải `a=1&a=2`: fastify trả
 *   kiểu KHÁC nhau cho một giá trị (`'x'`) và nhiều giá trị (`['x','y']`), và một ô lọc đang chọn
 *   đúng một mục sẽ lặng lẽ đi vào nhánh sai.
 */
const csvList = z.string().trim().min(1).optional()

export const ListInvoiceQuery = PageQuery.extend({
  customerId: blankAsAbsent(Uuid),
  status: blankAsAbsent(z.enum(['issued', 'partial', 'paid', 'cancelled'])),
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
  /** Ô 🔍 — số CT · số HĐ · tên · MST · mã đơn · mã khách. */
  q: z.string().trim().min(1).optional(),
  customerIds: csvList,
  salesNames: csvList,
  amis: blankAsAbsent(z.enum(['pushed', 'not'])),
  hasInvoiceNo: blankAsAbsent(z.enum(['has', 'no'])),
  payState: blankAsAbsent(z.enum(['paid', 'partial', 'unpaid'])),
  /** Màn Sổ chứng từ bỏ hẳn tờ đã huỷ; màn Hóa đơn bán thì không. */
  excludeCancelled: blankAsAbsent(z.enum(['1'])),
})

/** Cửa TÓM TẮT không lật trang — nó cộng và đếm, nên không nhận `limit`/`cursor`. */
export const ArRegisterSummaryQuery = ListInvoiceQuery.omit({ limit: true, cursor: true, dir: true })
export type ArRegisterSummaryQueryT = z.infer<typeof ArRegisterSummaryQuery>

/** Hàng chờ xuất hóa đơn — phân trang như mọi cửa danh sách khác. */
export const BillQueueQuery = PageQuery

/** Công nợ TẠI MỘT NGÀY. Bỏ trống `asOf` = hôm nay. */
export const DebtQuery = z.object({
  asOf: blankAsAbsent(ISODate),
  customerId: blankAsAbsent(Uuid),
})

export const IssueHintQuery = z.object({
  mgmtOnly: z.enum(['true', 'false']).optional(),
})

/** Ô số chứng từ tô đỏ NGAY khi gõ trùng (bản cũ `Az.tsx:19802` `docNoTaken`) — không đợi bấm Xuất mới biết. */
export const DocNoTakenQuery = z.object({
  docNo: z.string().trim().min(1),
})
export type DocNoTakenQueryT = z.infer<typeof DocNoTakenQuery>

/* ══ BẢNG KÊ BÁN (F16) ═══════════════════════════════════════════════════════════════════════ */

export const CreateStatementBody = z.object({
  jobIds: z.array(Uuid).min(1, 'Chưa chọn đơn nào để lập bảng kê.'),
  mode: BillMode.optional(),
  invName: blankAsAbsent(z.string()),
  invTax: blankAsAbsent(z.string()),
  invAddress: blankAsAbsent(z.string()),
  mgmtOnly: z.boolean().optional(),
  note: blankAsAbsent(z.string()),
})

export const SetStatementJobsBody = z.object({
  version: Version,
  jobIds: z.array(Uuid).min(1, 'Bảng kê phải có ít nhất một đơn.'),
})

export const StatementStepBody = z.object({ version: Version })

export const CancelStatementBody = z.object({
  version: Version,
  reason: z.string().min(1, 'Phải ghi lý do huỷ bảng kê.'),
})

/** Xuất hóa đơn CHO CẢ BẢNG KÊ — cùng bộ ô với xuất cho một đơn, chỉ khác nguồn. */
export const IssueStatementBody = z.object({
  statementId: Uuid,
  docDate: blankAsAbsent(ISODate),
  postDate: blankAsAbsent(ISODate),
  dueDate: blankAsAbsent(ISODate),
  invoiceNo: blankAsAbsent(z.string()),
  mgmtOnly: z.boolean().optional(),
  note: blankAsAbsent(z.string()),
  /** Số chứng từ gõ tay trên popup xuất (`IssueModal`) — trống thì tự nhảy. */
  docNo: blankAsAbsent(z.string().max(30)),
  /** Ô sửa TỪNG ĐƠN (`0111`): nội dung xuất HĐ · tên dịch vụ · BKS · TK công nợ / doanh thu / thuế. */
  lines: z.array(z.object({
    jobId: Uuid,
    label: blankAsAbsent(z.string().max(500)),
    serviceName: blankAsAbsent(z.string().max(500)),
    plate: blankAsAbsent(z.string().max(100)),
    acctReceivable: blankAsAbsent(z.string().max(20)),
    acctRevenue: blankAsAbsent(z.string().max(20)),
    acctVat: blankAsAbsent(z.string().max(20)),
  })).max(500).optional(),
})

/** ✏️ Đầu bảng kê trên popup chi tiết — cờ DX · số / ngày HĐ (đồng bộ xuống hóa đơn). */
export const UpdateStatementHeadBody = z.object({
  version: Version,
  mgmtOnly: z.boolean().optional(),
  invoiceNo: z.string().max(100).nullable().optional(),
  invoiceDate: blankAsAbsent(ISODate.nullable()),
})
export type UpdateStatementHeadBodyT = z.infer<typeof UpdateStatementHeadBody>

export const ListStatementQuery = PageQuery.extend({
  customerId: blankAsAbsent(Uuid),
  status: blankAsAbsent(z.enum(['draft', 'issued', 'cancelled'])),
})

export const CandidateQuery = z.object({ customerId: blankAsAbsent(Uuid) })

/** Bảng kê THU HỘ — một BÁO CÁO theo khoảng ngày, không phải bảng dữ liệu. */
export const OnBehalfQuery = z.object({
  from: ISODate,
  to: ISODate,
  customerId: blankAsAbsent(Uuid),
})

export const InvoiceIdParams = z.object({ id: Uuid })
export const JobIdParams = z.object({ id: Uuid })

/* ══ ĐỐI TRỪ CÔNG NỢ · KHÉP KÍN · NHẮC NỢ (F23) ══════════════════════════════════════════════ */

/**
 * ⚠ KHÔNG NHẬN `crossParty`. Đó là KẾT LUẬN từ dữ liệu (mã số thuế hai bên có khớp không), không
 *   phải lời khai của màn — và nó chính là thứ người duyệt cần lưu ý nhất. Để màn gửi lên là mở
 *   đường cho một phiếu đối trừ chéo tự dán nhãn "cùng đối tượng".
 */
export const CreateOffsetBody = z.object({
  arInvoiceId: Uuid,
  apInvoiceId: Uuid,
  amount: moneyField('Số tiền đối trừ'),
  debitAccount: blankAsAbsent(z.string()),
  creditAccount: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
})

export const OffsetStepBody = z.object({ version: Version })

export const RejectOffsetBody = z.object({
  version: Version,
  reason: z.string().min(1, 'Phải ghi lý do từ chối — người lập cần biết phải sửa gì.'),
})

export const ListOffsetQuery = PageQuery.extend({
  status: blankAsAbsent(z.enum(['pending', 'approved', 'rejected'])),
})

/**
 * ⚠ CÓ PHÂN TRANG dù màn hiện dùng nó làm ô chọn: số chứng từ mua CÒN NỢ lớn dần theo doanh số, và
 *   một ô chọn không phân trang là một cửa bốc cả bảng khi công ty chạy được vài năm.
 */
export const OffsetCandidateQuery = PageQuery

export const ClosedLoopQuery = z.object({
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
  /** Mốc lọc: ngày BỐC HÀNG thực tế (mặc định) hoặc ngày XUẤT HOÁ ĐƠN — bản cũ `AzClosedLoop.tsx:94`. */
  basis: z.enum(['pickup', 'invoice']).optional(),
})

export const DunningQuery = z.object({
  customerId: Uuid,
  asOf: blankAsAbsent(ISODate),
})

export type CreateOffsetBodyT = z.infer<typeof CreateOffsetBody>
export type ListOffsetQueryT = z.infer<typeof ListOffsetQuery>
export type DunningQueryT = z.infer<typeof DunningQuery>

export type RequestBillBodyT = z.infer<typeof RequestBillBody>
export type StatementRequestBodyT = z.infer<typeof StatementRequestBody>
export type StatementRequestPreviewQueryT = z.infer<typeof StatementRequestPreviewQuery>
export type ListInvoiceQueryT = z.infer<typeof ListInvoiceQuery>
export type DebtQueryT = z.infer<typeof DebtQuery>
export type CreateStatementBodyT = z.infer<typeof CreateStatementBody>
export type IssueStatementBodyT = z.infer<typeof IssueStatementBody>
export type ListStatementQueryT = z.infer<typeof ListStatementQuery>
export type OnBehalfQueryT = z.infer<typeof OnBehalfQuery>

/* ══ KHOÁ TÍN DỤNG & XIN GIA HẠN NỢ (`0067`) ═════════════════════════════════════════════════ */

export const RequestExtensionBody = z.object({
  customerId: Uuid,
  extensionDays: z.number().int()
    .min(1, 'Số ngày gia hạn phải từ 1 đến 30.')
    .max(30, 'Số ngày gia hạn phải từ 1 đến 30.'),
  reason: z.string().min(1, 'Phải ghi lý do xin gia hạn.'),
})

export const ApproveExtensionBody = z.object({ version: Version })
export const RejectExtensionBody = z.object({
  version: Version,
  reason: z.string().min(1, 'Phải ghi lý do từ chối.'),
})

/** Cửa đọc không có bộ lọc — màn hạn mức cần cả bảng cùng lúc để dựng trạng thái từng khách. */
export const ExtensionQuery = z.object({ status: blankAsAbsent(z.enum(['pending', 'approved', 'rejected'])) })

export const ExtensionIdParams = z.object({ id: Uuid })

export type RequestExtensionBodyT = z.infer<typeof RequestExtensionBody>
export type ApproveExtensionBodyT = z.infer<typeof ApproveExtensionBody>
export type RejectExtensionBodyT = z.infer<typeof RejectExtensionBody>
export type ExtensionQueryT = z.infer<typeof ExtensionQuery>

/* ══ HỘP THƯ ĐI (`0071`) ═════════════════════════════════════════════════════════════════════ */

/**
 * ⚠ `recipients` TUỲ CHỌN: bỏ trống thì `@rs/ar` lấy hai địa chỉ trong hồ sơ khách. Để màn tự gửi
 *   lên là hai chỗ cùng phải biết cột nào chứa thư của khách.
 */
export const QueueLetterBody = z.object({
  kind: z.enum(['debt_confirm', 'debt_statement']),
  customerId: Uuid,
  recipients: z.array(z.string()).optional(),
  asOf: blankAsAbsent(ISODate),
  subject: z.string().min(1, 'Thư phải có tiêu đề.'),
  body: z.string().min(1, 'Thư phải có nội dung.'),
  owedTotal: blankAsAbsent(moneyField('Tổng nợ')),
  overdueTotal: blankAsAbsent(moneyField('Nợ quá hạn')),
  invoiceCount: z.number().int().min(0).optional(),
})

export const QueueVendorLetterBody = z.object({
  kind: z.enum(['ap_debt_confirm', 'ap_debt_statement']),
  vendorId: Uuid,
  recipients: z.array(z.string()).optional(),
  asOf: blankAsAbsent(ISODate),
  subject: z.string().min(1, 'Thư phải có tiêu đề.'),
  body: z.string().min(1, 'Thư phải có nội dung.'),
  owedTotal: blankAsAbsent(moneyField('Tổng nợ')),
  overdueTotal: blankAsAbsent(moneyField('Nợ quá hạn')),
  invoiceCount: z.number().int().min(0).optional(),
})
export type QueueVendorLetterBodyT = z.infer<typeof QueueVendorLetterBody>

export const OutboxQuery = PageQuery.extend({ customerId: blankAsAbsent(Uuid) })

export type QueueLetterBodyT = z.infer<typeof QueueLetterBody>
export type OutboxQueryT = z.infer<typeof OutboxQuery>

/* ══ THEO DÕI PHẢI THU TIỀN CHI HỘ (`0073`) ══════════════════════════════════════════════════ */

/**
 * ⚠ KHÔNG CÓ Ô SỐ TIỀN. Số phải thu lấy từ `ar_invoice.on_behalf_total` — con số đã tính lúc xuất
 *   hoá đơn. Cho lời gọi tự khai là mở đường cho hai con số thu hộ của cùng một hoá đơn.
 */
export const ConfirmRecvBody = z.object({
  arInvoiceId: Uuid,
  postDate: blankAsAbsent(ISODate),
})

export const ReceiveRecvBody = z.object({
  version: Version,
  receivedDate: blankAsAbsent(ISODate),
  receivedAmount: moneyField('Số tiền báo có'),
  bankRef: z.string().min(1, 'Phải ghi số chứng từ / nội dung báo có.'),
})

export const RecvQuery = z.object({
  status: blankAsAbsent(z.enum(['pending', 'confirmed', 'received'])),
})

export const RecvIdParams = z.object({ id: Uuid })

export type ConfirmRecvBodyT = z.infer<typeof ConfirmRecvBody>
export type ReceiveRecvBodyT = z.infer<typeof ReceiveRecvBody>
export type RecvQueryT = z.infer<typeof RecvQuery>

/** 🔧 Điền bù hạn TT — không nhận tham số; thân rỗng vẫn khai để cổng kiểm lược đồ (`validate.ts`) không phải miễn trừ. */
export const FillDueBody = z.object({})
export type FillDueBodyT = z.infer<typeof FillDueBody>

/* ══ 🧾 TẠO CÔNG NỢ THỦ CÔNG · SỐ DƯ ĐẦU KỲ · SỔ 131 (`0109`) ═════════════════════════════════════════════════════ */

export const ManualInvoiceBody = z.object({
  customerId: Uuid,
  docDate: blankAsAbsent(ISODate),
  postDate: blankAsAbsent(ISODate),
  dueDate: blankAsAbsent(ISODate),
  invoiceNo: blankAsAbsent(z.string().max(100)),
  invoiceSymbol: blankAsAbsent(z.string().max(50)),
  invoiceFormNo: blankAsAbsent(z.string().max(50)),
  note: blankAsAbsent(z.string().max(1000)),
  lines: z.array(z.object({
    label: z.string().trim().min(1, 'Dòng thiếu tên hàng / dịch vụ.').max(500),
    quantity: z.number().positive(),
    priceVnd: z.number().nonnegative(),
    vatRate: z.number().min(0).max(100),
    unit: blankAsAbsent(z.string().max(50)),
    acctRevenue: blankAsAbsent(z.string().max(20)),
    /** Cột Mã hàng · Công trình (mã đơn, chữ tự do) · TK công nợ của bảng dòng bản cũ — cùng luật với sửa dòng (`UpdateArDocBody`). */
    itemCode: blankAsAbsent(z.string().max(100)),
    jobRef: blankAsAbsent(z.string().max(100)),
    acctReceivable: blankAsAbsent(z.string().max(20)),
  })).min(1).max(100),
})
export type ManualInvoiceBodyT = z.infer<typeof ManualInvoiceBody>

export const OpeningImportBody = z.object({
  asOf: ISODate,
  rows: z.array(z.object({
    name: z.string().trim().min(1).max(300),
    tax: blankAsAbsent(z.string().max(50)),
    amountVnd: z.number().positive(),
    dueDate: blankAsAbsent(ISODate),
    note: blankAsAbsent(z.string().max(500)),
  })).min(1).max(500),
})
export type OpeningImportBodyT = z.infer<typeof OpeningImportBody>

export const Ledger131Query = z.object({ customerId: Uuid })
export type Ledger131QueryT = z.infer<typeof Ledger131Query>
